import { CircularProgress } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getForms, reorderForm, sortForms } from '../../../redux/DynamicForms/DynamicFormsAction';
import FormRow from './FormRow';
import { MainDialog } from '../../UI';
import NewForm from './NewForm';
import MessagesContext from '../../../contexts/messagesContext';
import LanguageContext from '../../../contexts/languageContext';
import { getNamespace } from '../../../utils/storage';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import warningIcon from '../../../assets/images/Icon/warningIcon.svg';
import { PropTypes } from 'prop-types';
import { styles } from './formSetting.styles';
import { createLoadingSelector } from '../../../redux/Loading/LoadingSelector';
import { GET_ALL_FORMS } from '../../../redux/DynamicForms/DynamicFormsType';
import FormAndClinicHeader from '../FormAndClinicHeader';
import DownloadReports from '../DownloadReports';

const FormList = React.memo(function FormList({ data }) {
    return (
        data.length &&
        [...data]
            .sort((a, b) => a?.order - b?.order)
            ?.map((item, index) => <FormRow form={item} index={index} key={index} />)
    );
});
FormList.propTypes = {
    data: PropTypes.array
};
const FormsSetting = () => {
    const { messages } = useContext(MessagesContext);
    const { lang } = useContext(LanguageContext);
    const classes = styles({ rtl: lang === 'fa' });
    const [newFormDialogIsOpen, setNewFormDialogOpen] = useState();
    const dispatch = useDispatch();
    const loadingSelector = createLoadingSelector(GET_ALL_FORMS.REQUEST);
    const isLoading = useSelector(loadingSelector);
    const allForms = useSelector((state) => state.dynamicForms.allForms);
    const workspace = useSelector((state) => state?.user?.workspace?.clinic?.id);

    useEffect(() => {
        const namespace = getNamespace();
        if (namespace && workspace) {
            const params = { include_non_concrete: true };
            dispatch(getForms(namespace, params));
        }
    }, [workspace]);
    const toggleNewFormDialog = () => {
        setNewFormDialogOpen((newFormDialogIsOpen) => !newFormDialogIsOpen);
    };

    const reorder = (list, startIndex, endIndex) => {
        const result = list;
        const [removed] = result?.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };
    const onDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        if (result.destination.index === result.source.index) {
            return;
        }

        const sortedForm = [...allForms].sort((a, b) => a?.order - b?.order);
        const data = reorder(sortedForm, result.source.index, result.destination.index);
        const findForm = data[result.destination.index];

        const prevItem = data[result.destination.index - 1]?.order;
        const nextItem = data[result.destination.index + 1]?.order;

        if (sortedForm.every((item) => item?.order === 0)) {
            let forms = [];
            data.forEach((item, index) => {
                forms = [...forms, { ...item, order: index }];
            });
            if (forms.length) {
                dispatch(sortForms(sortedForm, forms));
            }
        } else {
            let reorderFormValue;

            if (result.destination.index === 0) {
                reorderFormValue = data[1]?.order - 0.01;
            } else if (result.destination.index === data.length - 1) {
                reorderFormValue = data[data.length - 2]?.order + 0.01;
            } else {
                reorderFormValue = (prevItem + nextItem) / 2;
            }
            const content = {
                name: findForm.name,
                order: reorderFormValue
            };

            dispatch(reorderForm(sortedForm, findForm.name, content));
        }
    };

    return (
        <>
            <DownloadReports />
            <div className={classes.formContainer}>
                {isLoading && allForms === null ? (
                    <div className="loadingContainer">
                        <CircularProgress className={classes.progress} />
                    </div>
                ) : (
                    <>
                        <FormAndClinicHeader
                            title={messages.permissionsTitle.manage_form}
                            buttonText={messages.newForm}
                            handleClick={toggleNewFormDialog}
                        />
                        <div>
                            {allForms && allForms.length ? (
                                <>
                                    <div className={classes.listHeader}>
                                        <div className={classes.formName}>
                                            <span>{messages.formInfo}</span>
                                        </div>
                                    </div>

                                    <>
                                        <DragDropContext onDragEnd={onDragEnd}>
                                            <Droppable droppableId="list">
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.droppableProps}
                                                    >
                                                        <FormList data={allForms} />
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </DragDropContext>
                                        <div className={classes.warningRow}>
                                            <img src={warningIcon} alt="!" />
                                            <span className={classes.warningText}>
                                                {messages.warningFormReport}
                                            </span>
                                        </div>
                                    </>
                                </>
                            ) : (
                                <div className={classes.emptyMessage}>
                                    <p>{messages.noFormSubmitted}</p>
                                </div>
                            )}
                        </div>
                        {newFormDialogIsOpen && (
                            <MainDialog
                                open={true}
                                title={messages.newForm}
                                goBack={toggleNewFormDialog}
                            >
                                <NewForm handleClose={toggleNewFormDialog} />
                            </MainDialog>
                        )}
                    </>
                )}
            </div>
        </>
    );
};
export default FormsSetting;
